import { useState } from "react";
import { Flag } from "lucide-react";
import { CreateLineItemFeedbackBodyCategory } from "@rebuild/shared/generated/models/createLineItemFeedbackBodyCategory";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitLineItemFeedbackMutation } from "@/hooks/mutation/feedback/useSubmitLineItemFeedbackMutation";
import type { LineItem } from "@/interfaces/estimate-detail";

const FEEDBACK_CATEGORIES = Object.values(CreateLineItemFeedbackBodyCategory);

interface LineItemFeedbackProps {
  estimateId: string;
  lineItem: LineItem;
  roomName: string;
  cat?: string;
  selCode?: string;
  onClose: () => void;
}

export function LineItemFeedback({ estimateId, lineItem, roomName, cat, selCode, onClose }: LineItemFeedbackProps) {
  const [selectedCategory, setSelectedCategory] = useState<CreateLineItemFeedbackBodyCategory | null>(null);
  const [comment, setComment] = useState("");
  const { mutate, isPending } = useSubmitLineItemFeedbackMutation();

  const handleSubmit = () => {
    if (!selectedCategory) return;

    const xactimateMapping = [cat, selCode].filter(Boolean).join(" > ") || undefined;

    mutate(
      {
        estimateId,
        lineItemId: lineItem.lineItemId,
        lineItemUuid: lineItem.uniqueId,
        category: selectedCategory,
        roomName,
        lineItemName: lineItem.lineItem,
        comment: comment || undefined,
        lineItemQuantity:
          lineItem.qnm?.[0]?.measurement && !isNaN(Number(lineItem.qnm[0].measurement)) ? Number(lineItem.qnm[0].measurement) : undefined,
        xactimateMapping,
      },
      {
        onSuccess: () => {
          setSelectedCategory(null);
          setComment("");
          onClose();
        },
      },
    );
  };

  return (
    <div className="w-80">
      <div className="text-muted-foreground mb-3 flex items-center gap-2">
        <Flag className="h-4 w-4" />
        <span className="text-sm font-medium">Report Issue</span>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {FEEDBACK_CATEGORIES.map((category) => (
          <Button
            key={category}
            variant={selectedCategory === category ? "accent" : "outline"}
            size="xs"
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </Button>
        ))}
      </div>

      <Textarea
        placeholder="Add details (required)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        className="mb-3"
        rows={2}
      />

      <Button onClick={handleSubmit} disabled={!selectedCategory || !comment.trim() || isPending} size="sm" className="w-full">
        {isPending ? "Submitting..." : "Submit"}
      </Button>
    </div>
  );
}
